import { apiRequest, getCurrentDate } from '../helpers'
import { Client } from '../react-app-env'

const kycCheckCache = new Map<string, { data: any; timestamp: number }>()
const CACHE_TTL = 5 * 60 * 1000
const pendingRequests = new Map<string, Promise<any>>()

export async function fetchActiveClientsCount(): Promise<number | null> {
  try {
    const response = await apiRequest(`${process.env.REACT_APP_API_URL}/client/count/active`, 'GET')
    return response?.ok ? response.count : null
  } catch {
    return null
  }
}

export async function softDeleteClient(clientId: string, clientNumber: number) {
  const response = await apiRequest(`${process.env.REACT_APP_API_URL}/client`, 'PATCH', {
    clientId,
    clientNumber,
    deletedAt: getCurrentDate(),
  })

  return response
}

export async function setClientActiveStatus(client: Client, active: boolean | undefined) {
  const response = await apiRequest(`${process.env.REACT_APP_API_URL}/client`, 'PATCH', {
    clientId: client._id,
    clientNumber: client.clientNumber,
    active,
    deletedAt: '',
  })

  return response
}

export async function validatePinCode(pinCode: number) {
  const response = await apiRequest(
    `${process.env.REACT_APP_API_URL}/user/pincode-validation`,
    'POST',
    { pinCode }
  )

  return response
}

export async function validatePinCodeClient(pinCode: number, email?: string) {
  const response = await fetch(`${process.env.REACT_APP_API_URL}/user/pincode-validation/client`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ pinCode, email }),
  })

  return response.json()
}

export async function fetchAccess(email: string | null) {
  const response = await apiRequest(`${process.env.REACT_APP_API_URL}/access/get/request`, 'POST', {
    email,
  })

  return response
}

export async function fetchAccessForClient(email: string | null) {
  const response = await apiRequest(`${process.env.REACT_APP_API_URL}/access/get/client`, 'POST', {
    email,
  })

  return response
}

export async function performKycCheckOne(client: {
  clientId: string | null
  clientNumber: string | null
  firstName: string | null
  lastName: string | null
  dateOfBirth: string | null
  country: string | null
  accessToken: string
  companyInfo: any
  clientInfo?: boolean
  clientRepresentId?: string
  clientInfoId?: string
}) {
  const {
    clientId,
    clientNumber,
    firstName,
    lastName,
    dateOfBirth,
    country,
    accessToken,
    clientInfo,
    clientRepresentId,
    clientInfoId,
  } = client

  const response = await apiRequest(`${process.env.REACT_APP_API_URL}/schufa/search`, 'POST', {
    clients: [
      {
        clientId,
        clientNumber,
        begriffe: `${firstName} ${lastName}`,
        dateOfBirth,
        land: country,
        clientInfoId: clientInfoId ?? null,
      },
    ],
    accessToken,
    clientInfo: clientInfo ? true : false,
    clientRepresentId: clientRepresentId ?? null,
  })

  if (response.ok) {
    return response.results
  }
}

export async function performKycCheckMany(
  clients: any[],
  accessToken: string,
  serviceType: 'schufaOne' | 'schufaMany',
  companyInfo: any
) {
  const response = await apiRequest(`${process.env.REACT_APP_API_URL}/schufa/search`, 'POST', {
    clients,
    accessToken,
  })

  return response
}

export async function performKycCheckOneByClientId(clientId: string, accessToken: string) {
  return apiRequest(`${process.env.REACT_APP_API_URL}/schufa/search`, 'POST', {
    clientId,
    accessToken,
  })
}

export async function performAmlCheckMany(clients: any[], accessToken: string) {
  const response = await apiRequest(`${process.env.REACT_APP_API_URL}/aml/search`, 'POST', {
    clients,
    accessToken,
  })

  return response
}

export async function performAmlCheckOneByClientId(clientId: string, accessToken: string) {
  return apiRequest(`${process.env.REACT_APP_API_URL}/aml/search`, 'POST', {
    clientId,
    accessToken,
  })
}

export async function createIdentityRequestService(payload: {
  service: string
  identityData: any
}) {
  const { identityData } = payload

  const identityResponse = await apiRequest(
    `${process.env.REACT_APP_API_URL}/identity/create`,
    'POST',
    identityData
  )

  if (!identityResponse.ok) {
    return { error: 'identity_failed' }
  }

  return { success: true }
}

export async function deleteIdentityRequestService(identityData: {
  identityCompanyId?: string
  identityRequestId?: string
  clientId?: string
  subjectId?: string
}) {
  const response = await apiRequest(
    `${process.env.REACT_APP_API_URL}/identity/delete`,
    'POST',
    identityData
  )

  if (!response?.ok) {
    return { error: 'identity_delete_failed' }
  }

  return { success: true }
}

export async function fetchClients(filter: any) {
  const response = await apiRequest(`${process.env.REACT_APP_API_URL}/clients`, 'POST', filter)
  return response
}

export async function fetchUserEmail() {
  const response = await apiRequest(`${process.env.REACT_APP_API_URL}/user/get`, 'POST')
  return response
}

export async function fetchClientByNumber(clientNumber: string) {
  const response = await apiRequest(
    `${process.env.REACT_APP_API_URL}/client/number?clientNumber=${clientNumber}`,
    'GET'
  )
  return response
}

export async function fetchKycChecks(clientId: string) {
  const response = await apiRequest(
    `${process.env.REACT_APP_API_URL}/schufa/kyc/${clientId}`,
    'GET'
  )
  return response
}

export async function fetchAmlChecks(clientId: string) {
  const response = await apiRequest(
    `${process.env.REACT_APP_API_URL}/aml/checks/${clientId}`,
    'GET'
  )
  return response
}

// How many people the AML check will screen (client + representatives +
// beneficial owners from the Erhebungsbogen), how many of them are already
// covered by today's cache (not charged), and the resulting point cost.
export async function fetchAmlCheckCost(clientId: string): Promise<{
  ok: boolean
  count: number
  cachedCount: number
  billableCount: number
  points: number
}> {
  return apiRequest(`${process.env.REACT_APP_API_URL}/aml/checks/cost/${clientId}`, 'GET')
}

export async function updateAmlHitStatus(amlCheckId: string, hitId: string, deleted: boolean) {
  const response = await apiRequest(
    `${process.env.REACT_APP_API_URL}/aml/checks/hit/status`,
    'POST',
    {
      amlCheckId,
      hitId,
      deleted,
    }
  )

  return response
}

export async function fetchAmlMonitoring(clientId: string) {
  const response = await apiRequest(
    `${process.env.REACT_APP_API_URL}/aml/monitoring/${clientId}`,
    'GET'
  )

  return response
}

export async function setAmlMonitoringStatus(
  clientId: string,
  amlCheckId: string,
  enabled: boolean
) {
  const response = await apiRequest(`${process.env.REACT_APP_API_URL}/aml/monitoring`, 'POST', {
    clientId,
    amlCheckId,
    enabled,
  })

  return response
}

export async function fetchIdentityParams(clientId: string | null, subjectId?: string | null) {
  const query = subjectId
    ? `${process.env.REACT_APP_API_URL}/identity/get?clientId=${clientId}&subjectId=${subjectId}`
    : `${process.env.REACT_APP_API_URL}/identity/get?clientId=${clientId}`
  const response = await apiRequest(query, 'GET')
  return response
}

export async function updateKycStatus(
  clientId: string | undefined,
  kycNumber: number,
  status: string
) {
  const response = await apiRequest(`${process.env.REACT_APP_API_URL}/schufa/kyc/status`, 'POST', {
    clientId,
    kycNumber,
    status,
  })

  return response
}

export async function fetchIdentityPdf(companyId: string, requestId: string) {
  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/identity/ds-identity/pdf?companyId=${companyId}&requestId=${requestId}`,
    {
      method: 'GET',
      headers: {
        Accept: 'application/pdf',
      },
    }
  )

  return response
}

export async function fetchIdentityVideo(companyId: string, requestId: string) {
  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/identity/ds-identity/video?companyId=${companyId}&requestId=${requestId}`,
    {
      method: 'GET',
      headers: {
        Accept: 'application/mp4',
      },
    }
  )

  return response
}

export async function createClient(clientData: any) {
  const response = await fetch(`${process.env.REACT_APP_API_URL}/client`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(clientData),
  })

  return response.json()
}

export async function updateClient(clientData: any) {
  const response = await fetch(`${process.env.REACT_APP_API_URL}/client`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(clientData),
  })

  return response.json()
}

export async function updateStammdatenClient(clientData: any) {
  const response = await fetch(`${process.env.REACT_APP_API_URL}/client/stammdaten`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(clientData),
  })

  return response.json()
}

export async function fetchClientById(clientId: string) {
  const response = await fetch(`${process.env.REACT_APP_API_URL}/client/${clientId}`)
  return response.json()
}

export async function getStammdatenClients(filter: any) {
  const response = await apiRequest(`${process.env.REACT_APP_API_URL}/clients`, 'POST', filter)

  return response
}

export async function getKycCheckClientInfo(id: string) {
  const cacheKey = `kyc-check-${id}`

  const cached = kycCheckCache.get(cacheKey)
  if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
    return cached.data
  }

  if (pendingRequests.has(cacheKey)) {
    return pendingRequests.get(cacheKey)
  }

  const requestPromise = apiRequest(
    `${process.env.REACT_APP_API_URL}/schufa/client-info/${id}`,
    'GET'
  )
    .then(response => {
      if (response.ok) {
        kycCheckCache.set(cacheKey, { data: response, timestamp: Date.now() })
      }
      pendingRequests.delete(cacheKey)
      return response
    })
    .catch(error => {
      pendingRequests.delete(cacheKey)
      throw error
    })

  pendingRequests.set(cacheKey, requestPromise)
  return requestPromise
}

export function clearKycCheckCache(id?: string) {
  if (id) {
    kycCheckCache.delete(`kyc-check-${id}`)
  } else {
    kycCheckCache.clear()
  }
}

export async function updateClientsStatus(
  clients: { clientId: string; updates: Partial<Client> }[]
) {
  const response = await apiRequest(`${process.env.REACT_APP_API_URL}/clients`, 'PATCH', {
    clients,
  })

  return response
}

export function clientDelete(clientId: string) {
  return apiRequest(`${process.env.REACT_APP_API_URL}/client/${clientId}`, 'DELETE')
}

export async function updateClientRisk(clientId: string, risik: string) {
  return apiRequest(`${process.env.REACT_APP_API_URL}/client/risk`, 'PATCH', {
    clientId,
    risik,
  })
}
